// dom: the few helpers every module shares. Text is always added as text, never parsed as HTML.

/**
 * An element with an optional class and children. Strings become text nodes; empty values are skipped.
 * @template {keyof HTMLElementTagNameMap} K
 * @param {K} tag
 * @param {string} [cls]
 * @param {...(Node | string | false | null | undefined)} kids
 * @returns {HTMLElementTagNameMap[K]}
 */
export function el(tag, cls, ...kids) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    for (const k of kids) if (k) e.append(k);
    return e;
}

/**
 * The URL when it is https, else ''. Links and images come from our server, but we check anyway.
 * Plain http only for localhost, the visitor's own machine: our own links on a local run.
 * @param {unknown} url
 */
export function https(url) {
    try {
        const u = new URL(String(url));
        return u.protocol === 'https:' || (u.protocol === 'http:' && /^(localhost|127\.0\.0\.1)$/.test(u.hostname)) ? String(url) : '';
    } catch {
        return '';
    }
}

/**
 * Set inline styles, touching only what changed, so an idle page sees no mutations from us.
 * Property by property (not a style="" string), which a strict style-src CSP allows.
 * @param {HTMLElement} e
 * @param {Record<string, string>} props  CSS property → value; '' removes it
 */
export function css(e, props) {
    for (const [k, v] of Object.entries(props)) {
        if (e.style.getPropertyValue(k) !== v) e.style.setProperty(k, v);
    }
}

/**
 * Set or remove a data-* attribute, only when it changes.
 * @param {HTMLElement} e
 * @param {string} key  camelCase, as in dataset
 * @param {string} value  '' removes it
 */
export function data(e, key, value) {
    if (value && e.dataset[key] !== value) e.dataset[key] = value;
    else if (!value && key in e.dataset) delete e.dataset[key];
}

/**
 * The element a selector finds, or null when it is missing or invalid.
 * @param {string | undefined} selector
 */
export function find(selector) {
    try {
        return selector ? document.querySelector(selector) : null;
    } catch {
        return null;
    }
}
